



















import {
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
} from '@nuxtjs/composition-api'

import { throttle } from 'throttle-debounce'

export type SliderState = {
  active: boolean
  position: number
  startPosition: number
  touchPositionX: number
  touchPositionY: number
  startTouchPositionX: number
  dragDistance: number
  scrollWidth: number
  offsetWidth: number
}

const getState = (): SliderState => ({
  active: false,
  position: -1,
  startPosition: -1,
  clientX: -1,
  clientY: -1,
  startClientX: -1,
  dragDistance: -1,
  scrollWidth: 0,
  offsetWidth: 0,
})

export default defineComponent({
  name: 'BlockSlider',
  props: {
    scrollSpeed: {
      type: Number,
      default: 1,
    },
    dragThreshold: {
      type: Number,
      default: 10,
    },
    skipSlideThreshold: {
      type: Number,
      default: 3,
    },
  },
  setup(props, { emit }) {
    const sliderState = getState()
    let updateId = null
    const slider = ref<HTMLDivElement>(null)
    const sliderContainer = ref<HTMLDivElement>(null)
    const active = ref(false)

    const setActive = (value: boolean) => {
      active.value = value
      sliderState.active = value
    }

    const getPosition = (e) => {
      let positionX, positionY
      if (e.type.includes('touch')) {
        positionX = e.touches[0].clientX
        positionY = e.touches[0].clientY
      }
      if (e.type.includes('mouse')) {
        positionX = e.clientX
        positionY = e.clientY
      }
      return [positionX, positionY]
    }

    const onMouseDown = (e) => {
      sliderState.startPosition = sliderState.position
      const [positionX, positionY] = getPosition(e)
      sliderState.touchPositionX = positionX
      sliderState.startTouchPositionX = positionX
      sliderState.touchPositionY = positionY
      sliderState.dragDistance = 0
      setActive(true)
    }

    const onMouseUp = () => {
      setActive(false)
    }

    const onMouseLeave = () => {
      setActive(false)
    }

    const onMouseMove = (e) => {
      if (!sliderState.active) return
      const [positionX, positionY] = getPosition(e)
      const deltaX = Math.abs(sliderState.touchPositionX - positionX)
      const deltaY = Math.abs(sliderState.touchPositionY - positionY)
      sliderState.touchPositionX = positionX
      sliderState.touchPositionY = positionY
      sliderState.dragDistance += deltaX + deltaY
    }

    const onDragStart = (e) => {
      e.preventDefault()
    }

    const clampPosition = (value) => {
      const maxPosition = sliderState.scrollWidth - sliderState.offsetWidth
      return Math.max(0, Math.min(maxPosition, value))
    }

    let animateTimeout = null

    const setPosition = (value: number, animate: boolean) => {
      value = clampPosition(value)
      sliderState.position = value
      slider.value.style.transform = `translateX(${-value}px)`
      if (animate && !active.value) {
        clearTimeout(animateTimeout)
        slider.value.classList.add('block-slider-animate')
        animateTimeout = setTimeout(() => {
          slider.value.classList.remove('block-slider-animate')
        })
      }
    }

    const getLimit = () => {
      let min = true
      let max = true
      if (slider.value) {
        min = sliderState.position <= 0
        max =
          sliderState.position >=
          sliderState.scrollWidth - sliderState.offsetWidth
      }
      return { min, max }
    }

    const reportLimit = () => {
      emit('limit-reached', getLimit())
    }

    const throttledLimit = throttle(500, reportLimit)

    const animate = () => {
      if (slider.value && sliderState.active) {
        const distance =
          (sliderState.touchPositionX - sliderState.startTouchPositionX) *
          props.scrollSpeed
        setPosition(sliderState.startPosition - distance, false)
        throttledLimit()
      }
      updateId = requestAnimationFrame(animate)
    }

    onMounted(() => {
      nextTick(() => {
        animate()
        reportLimit()
        sliderState.scrollWidth = slider.value.scrollWidth
        sliderState.offsetWidth = slider.value.offsetWidth
      })
    })

    onUnmounted(() => {
      cancelAnimationFrame(updateId)
    })

    const onClick = (e) => {
      if (sliderState.dragDistance > props.dragThreshold) {
        e.stopPropagation()
        e.preventDefault()
      }
      sliderState.dragDistance = 0
    }

    onMounted(() => {
      document.body.addEventListener('click', onClick, true)
    })

    const handleMacScroll = (e) => {
      e.stopPropagation()
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && e.deltaMode === 0) {
        e.preventDefault()
        setPosition(sliderState.position + e.deltaX, false)
        reportLimit()
      }
    }

    onMounted(() => {
      sliderContainer.value.addEventListener('wheel', handleMacScroll, {
        passive: false,
        capture: true,
      })
    })

    onUnmounted(() => {
      sliderContainer.value.removeEventListener('wheel', handleMacScroll, {
        passive: false,
        capture: true,
      })
    })

    const sortedIndex = (arr, num, dir) => {
      for (let i = 0; i < arr.length; i += 1) {
        if (arr[i] >= num) {
          return dir < 0 ? i : Math.max(0, i - 1)
        }
      }
      return arr.length - 1
    }
    const inRange = (value, min, max) => {
      return value >= min && value <= max
    }

    const goToHandler = (direction) => {
      return () => {
        if (!slider.value) return
        const items = Array.from(slider.value.children).map(
          (el, _, arr) => el.offsetLeft - arr[0].offsetLeft
        )
        const index = sortedIndex(items, sliderState.position, direction)
        let nextPosition = direction > 0 ? Infinity : -Infinity
        let nextIndex = direction > 0 ? items.length - 1 : 0
        const lookUpNext = 1 * direction + index
        const lookupAfterNext = 2 * direction + index
        if (inRange(lookUpNext, 0, items.length - 1)) {
          nextIndex = lookUpNext
          nextPosition = items[nextIndex]
          if (
            Math.abs(sliderState.position - nextPosition) <
              props.skipSlideThreshold &&
            inRange(lookupAfterNext, 0, items.length - 1)
          ) {
            nextIndex = lookupAfterNext
            nextPosition = items[nextIndex]
          }
        }
        setPosition(nextPosition, true)
        emit('index-changed', { index, limit: getLimit() })
        reportLimit()
      }
    }

    const goToNext = goToHandler(1)
    const goToPrevious = goToHandler(-1)

    return {
      slider,
      sliderContainer,
      onMouseDown,
      onMouseUp,
      onMouseLeave,
      onMouseMove,
      onDragStart,
      onClick,
      active,
      goToNext,
      goToPrevious,
    }
  },
})
